import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { StudentClassDetail } from '../types';
import { useSession } from '../context/SessionContext';
import { ApiError, portalApi } from '../utils/api';
import { formatScheduleRange } from '../utils/assessmentSchedule';

function formatDate(value?: string | null) {
  if (!value) return 'N/A';

  // Keep DB date values stable across timezones by using the YYYY-MM-DD portion as a local calendar date.
  const isoDateParts = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/);
  if (isoDateParts) {
    const year = Number(isoDateParts[1]);
    const monthIndex = Number(isoDateParts[2]) - 1;
    const day = Number(isoDateParts[3]);
    const localDate = new Date(year, monthIndex, day);
    if (!Number.isNaN(localDate.getTime())) {
      return localDate.toLocaleDateString();
    }
  }

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return direct.toLocaleDateString();
  }

  return 'N/A';
}

function classTitle(item: StudentClassDetail) {
  return `${item.class_name || item.course_code} (${item.section_code})`;
}

function availabilityLabel(status?: string) {
  if (status === 'coming_soon') return 'Coming Soon';
  if (status === 'closed') return 'Inactive';
  return 'Active';
}

function availabilityColor(status?: string): 'success' | 'warning' | 'default' {
  if (status === 'active' || !status) return 'success';
  if (status === 'coming_soon') return 'warning';
  return 'default';
}

export default function StudentClassPage() {
  const navigate = useNavigate();
  const { refreshSession } = useSession();
  const { sectionId } = useParams<{ sectionId: string }>();
  const [error, setError] = useState<string | null>(null);

  const { data: detail, isLoading } = useQuery<StudentClassDetail>({
    queryKey: ['portal', 'student', 'class', sectionId],
    queryFn: () => portalApi.getStudentClassDetail(sectionId!),
    enabled: !!sectionId,
  });

  const startMutation = useMutation({
    mutationFn: (assessmentId: string) => portalApi.startStudentClassAssessment(sectionId!, assessmentId),
    onSuccess: async () => {
      await refreshSession();
      navigate('/assessment');
    },
    onError: (mutationErr) => {
      setError(mutationErr instanceof ApiError ? mutationErr.message : 'Unable to start this assessment');
    },
  });

  const handleStartAssessment = (assessmentId: string) => {
    setError(null);
    startMutation.mutate(assessmentId);
  };

  if (isLoading) {
    return <Typography sx={{ py: 4 }}>Loading class details...</Typography>;
  }

  if (!detail) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Class not found'}</Alert>
      </Box>
    );
  }

  const completedAssessmentIds = new Set(
    detail.completed_assessments
      .map((item) => item.assessment_id)
      .filter((assessmentId): assessmentId is string => Boolean(assessmentId))
  );

  const availableAssessments = detail.assessments.filter(
    (assessment) => !completedAssessmentIds.has(assessment.assessment_id)
  );

  return (
    <Box sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" fontWeight={700}>Class Details</Typography>
          <Button variant="outlined" onClick={() => navigate('/student-portal')}>
            Back to Student Portal
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>{classTitle(detail)}</Typography>
            <Typography color="text.secondary">Term: {detail.term}</Typography>
            <Typography color="text.secondary">Course: {detail.course_code}</Typography>
            <Typography color="text.secondary">Institution: {detail.institution || 'N/A'}</Typography>
            <Typography color="text.secondary">Educator: {detail.faculty_name || 'N/A'}</Typography>
            <Typography color="text.secondary">Start Date: {formatDate(detail.start_date)}</Typography>
            <Typography color="text.secondary">End Date: {formatDate(detail.end_date)}</Typography>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Available Assessments</Typography>
            {availableAssessments.length === 0 ? (
              <Typography color="text.secondary">No available assessments right now.</Typography>
            ) : (
              <List>
                {availableAssessments.map((assessment) => (
                    <ListItem key={`${assessment.assessment_id}-${assessment.assigned_at || ''}`} divider>
                      <ListItemText
                        primary={assessment.name}
                        secondary={`${assessment.course_level || 'Course level N/A'} • Version ${assessment.version || 'N/A'} • ${formatScheduleRange(assessment.opens_at, assessment.closes_at, assessment.schedule_timezone)}`}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        {assessment.availability_status && assessment.availability_status !== 'active' && (
                          <Chip
                            label={availabilityLabel(assessment.availability_status)}
                            color={availabilityColor(assessment.availability_status)}
                            size="small"
                          />
                        )}
                        {(assessment.availability_status === 'active' || !assessment.availability_status) && (
                          <Button
                            variant="contained"
                            onClick={() => void handleStartAssessment(assessment.assessment_id)}
                            disabled={startMutation.isPending}
                          >
                            {assessment.in_progress ? 'Continue' : 'Start'}
                          </Button>
                        )}
                      </Stack>
                    </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" mb={2}>Completed Assessment Feedback</Typography>
            {detail.completed_assessments.length === 0 ? (
              <Typography color="text.secondary">No completed assessments yet.</Typography>
            ) : (
              <List>
                {detail.completed_assessments.map((item) => (
                  <ListItem key={item.session_id} divider>
                    <ListItemText
                      primary={item.assessment_name || 'Assessment'}
                      secondary={`Completed: ${formatDate(item.completed_at)}`}
                    />
                    {item.has_feedback ? (
                      <Button
                        variant="outlined"
                        onClick={() => navigate(`/student-portal/classes/${detail.section_id}/feedback/${item.session_id}`)}
                      >
                        View Feedback
                      </Button>
                    ) : (
                      <Chip label="Feedback not available" size="small" />
                    )}
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
